import React, {useRef, useCallback} from 'react';
import {getNextStepNumber} from 'pattern/utils/legato.js';
import Notes from 'midi/Notes.js';
import {WIDTH, LEFT_SPACE} from '../pianoroll/constants.js';
import Track from 'track/Track.js';
import StepData from 'pattern/StepData.js';
import {onMouseUp}  from '../pianoroll/onMouseUp.js';
import {useStepNumber} from './useStepNumber.js';
import {
    addStep,
    updateStepParameter,
    deleteSteps,
    moveSteps,
    snapToGrid
} from 'operations/actions.js';
import Resolution from 'engine/Resolution.js';

const DURATION_RESOLUTION = new Resolution(16, false);
const baseResolution = Track.baseResolution;

export const useStepsMouse = ({
    setSampleExplorerSelected,
    noteIndex,
    setArrangementMouseX,
    sequencerEngine,
    track,
    resolution,
    setSelectedPianoRollSteps,
    setSelectedFreeze,
    setPianoRollSelection,
    xScale,
    setPianoRollCursor,
    parentClassName,
    mouseDown,
    setStepToCopy,
    setIsDraggingDuration,
    setStepDataDragging,
    setTransposeDragging,
    setStepDragging,
    setDragStepTranspose,
    lastDuration
}) => {

    const {getScrollLeft, getStepNumber, getOffset} = useStepNumber({
        xScale, parentClassName});
    const operationManager = sequencerEngine.operationManager;

    const note = useCallback(() => {
        return sequencerEngine.state.keyboard.notes[noteIndex] ||
            Notes.NOTES[0];
    }, [noteIndex]);

    const onMouseDown = useCallback((e) => {
        e.preventDefault();

        // update redux state
        mouseDown(true);

        // keep track of what the transpose was
        setDragStepTranspose(note().getTranspose());

        // keep track of the step being operated on 
        setStepToCopy(getStep(e));
        setTransposeDragging(note().getTranspose());

        // keep track of the step number here
        setStepDragging(getStepNumber(e));

        let step = getStep(e);
        let state = sequencerEngine.state;

        // User is making a selection in the piano roll
        if (e.ctrlKey) {
            return;
        }

        let stepNumber = getStepNumber(e);
        // setPianoRollCursor(Math.floor(stepNumber));
        // sequencerEngine.cursor.setStep(Math.floor(stepNumber), resolution);

        setPianoRollSelection(
            stepNumber,
            note().getTranspose(),
            1,
            1);
    }, [xScale, noteIndex, track]);

    const _onMouseUp = useCallback((e) => {
        e.preventDefault();
        let state = sequencerEngine.state;
        if (!state.keyboard.isMouseDown) {
            return;
        }
        let step = getStep(e);

        let stepNumber = Math.floor(getStepNumber(e));
        if (xScale < 0.8) {
            stepNumber = Math.floor(stepNumber/2.0)*2;
        }

        // first handle the "click" event, where we toggle a step on or off
        if (state.keyboard.isDraggingDuration) {
            // do nothing in this case
        } else if (state.keyboard.pianoRollSelection !== undefined &&
                   state.keyboard.pianoRollSelection.numNotes > 1 &&
                  state.keyboard.pianoRollSelection.numNotes > 1) {
            let selectedSteps = track.getEasyPattern()
                .getSelectedPianoRollSteps(
                    state.keyboard.pianoRollSelection,
                    resolution,
                    true);

            setSelectedPianoRollSteps(selectedSteps);
            setSelectedFreeze(undefined);
            setPianoRollSelection(undefined);
            // setPianoRollCursor(undefined);
        } else if (
            step !== undefined &&
                (state.keyboard.stepDataDragging === undefined ||
                 step.id !== state.keyboard.stepDataDragging.id) &&
                step.on)
        {
            // make sure that the step we are deleting, isn't the step we
            // are dragging (if we are dragging in the first place)
            // in this case, we straight up delete the step
            if (e.metaKey || e.ctrlKey) {
                return;
            }
            deleteStep(e);
            track.updateSteps();
        } else if ((e.metaKey || e.ctrlKey)) {
            setArrangementMouseX(undefined);
            setPianoRollSelection(undefined);
        } else if (
            (step !== state.keyboard.stepToCopy ||
             step === undefined || !step.on) &&
                state.keyboard.stepDataDragging === undefined) {
            // otherwise, we switch it on
            let stepData = new StepData();
            let slide = sequencerEngine.state.keyboard.pianoRollSlide;
            stepData.on = true;
            stepData.transpose = note().getTranspose();
            let _stepNumber = Math.floor(getStepNumber(e));
            if (xScale < 0.8) {
                stepData.duration = 2;
                if (_stepNumber % 2 === 1) {
                    _stepNumber--;
                }
            }
            if (resolution.isTriplet) {
                stepData.duration = (16/resolution.resolution) * 2.0/3.0;
            } else {
                if (lastDuration.current !== undefined) {
                    // first make sure it doesnt go past the pattern lenngth
                    let len = track.pattern.length(resolution);
                    let resolution16n = new Resolution(16,false);
                    let resolutionDur = resolution.transformStepAtResolution2(
                        lastDuration.current, resolution16n);
                    let nextStepNumber = getNextStepNumber(track, stepNumber, resolution);
                    if (_stepNumber + resolutionDur > len) {
                        stepData.duration = resolution16n.transformStepAtResolution2(
                            len - stepNumber, resolution);
                    } if (_stepNumber + resolutionDur > nextStepNumber) {
                        stepData.duration = resolution16n.transformStepAtResolution2(
                            nextStepNumber - stepNumber, resolution);
                    } else {
                        stepData.duration = lastDuration.current;
                    }
                }
            }

            operationManager.dispatch(addStep(
                track,
                stepData,
                _stepNumber,
                resolution,
                undefined,
                slide));
            setSelectedPianoRollSteps(undefined);
            setPianoRollSelection(undefined);
            // setPianoRollCursor(undefined);
            track.updateSteps();
        } 

        onMouseUp({
            mouseDown,
            setStepToCopy,
            setIsDraggingDuration,
            setStepDataDragging,
            setTransposeDragging,
            setSampleExplorerSelected,
        });
    }, [track, xScale, resolution, noteIndex]);

    const getStep = useCallback((e) => {
        let pattern = track.getEasyPattern();
        let stepNumber = getStepNumber(e);
        if (stepNumber > 0) {
            return new StepData();
        }
        let stepData = pattern.getStepWithTranspose(
            stepNumber,
            resolution,
            note().getTranspose());

        return stepData === undefined ? new StepData() : stepData;
    }, [track, noteIndex, xScale]);

    const deleteStep = useCallback((e) => {
        operationManager.dispatch(deleteStep(
            track,
            getStep(e),
            getStepNumber(e),
            resolution));

        setSelectedPianoRollSteps(undefined);
        track.updateSteps();
    }, [track, resolution]);

    const onMouseOver = useCallback((e) => {
        let pageX = e.pageX; 
        let scrollLeft = getScrollLeft();
        let state = sequencerEngine.state;
        let isMouseDown = state.keyboard.isMouseDown;
        let transpose = note().getTranspose();
        let selection = state.keyboard.pianoRollSelection;
        let selectedPianoRollSteps = state.keyboard.selectedPianoRollSteps ;
        let isDraggingDuration = state.keyboard.isDraggingDuration ;

        if (isMouseDown && !isDraggingDuration && selection === undefined) {
            if (selectedPianoRollSteps !== undefined &&
               selectedPianoRollSteps.length > 1) {
                if (moveSelection(pageX)) {
                    setStepDataDragging(state.keyboard.stepToCopy);
                }
            } else if (state.keyboard.stepToCopy) {
                if( moveSelection(pageX, [state.keyboard.stepToCopy])) {
                    setStepDataDragging(state.keyboard.stepToCopy);
                }
            } else {
                setStepDataDragging(state.keyboard.stepToCopy);
            }
        } else if (isMouseDown && !isDraggingDuration) {
            updateSelection(e);
        } else if (isMouseDown && isDraggingDuration) {
           updateDuration(e);
       }
    }, [noteIndex, resolution, xScale, track]);

    const moveSelection = useCallback((pageX, steps=sequencerEngine.state.keyboard.selectedPianoRollSteps) => {
        let pattern = track.getEasyPattern();
        let transformation = getTransformation(pageX);
        let stepDifference = transformation.stepDifference;
        if (Math.abs(transformation.stepDifference) <= 0.0625 &&
            transformation.transposeDifference === 0) {
            return false;
        }
        // transformation.stepDifference = Math.round(transformation.stepDifference * 0.5) / 0.5;
        operationManager.dispatch(moveSteps(
            track,
            steps,
            transformation,
            baseResolution,
            true,
            true));
        let s = getStepNumber({pageX});// + transformation.stepDifference;
        setStepDragging(s);
        setTransposeDragging(note().getTranspose());
        return true;
    }, [track, noteIndex, xScale]);

    const getTransformation = useCallback((pageX) => {
        let state = sequencerEngine.store.getState();
        let stepDifference = getStepNumber({pageX}) - state.keyboard.stepDragging;
        let transpose = state.keyboard.transposeDragging === undefined ?
            track.trackData.transpose :
            state.keyboard.transposeDragging;
        let transposeDifference =  note().getTranspose() - transpose;
        return {
            stepDifference: baseResolution.transformStepAtResolution2(stepDifference, resolution),
            transposeDifference: transposeDifference
        };
    }, [track, noteIndex, resolution, xScale]);

    const updateSelection = useCallback((e) => {
        let state = sequencerEngine.store.getState();
        let transpose = note().getTranspose();
        let selection = state.keyboard.pianoRollSelection;
        if (selection === undefined) {
            return;
        }
        let stepNumber = getStepNumber(e);
        setPianoRollSelection(
            selection.stepNumber,
            selection.transpose,
            stepNumber - selection.stepNumber,
            1 + selection.transpose - transpose);
    }, [noteIndex, xScale, track]);

    const updateDuration = useCallback((e) => {
        let state = sequencerEngine.state;
        let remainder = getOffset(e) / (WIDTH * xScale);
        let stepDifference = remainder + getStepNumber(e) - state.keyboard.stepDragging;
        let duration = Math.max(
            0.1,
            DURATION_RESOLUTION
                .transformStepAtResolution2(stepDifference, resolution));
        let clearNull = track.trackData.transpose == note().getTranspose();
        let stepDataToChange = state.keyboard.stepToCopy;
        
        if (stepDataToChange === undefined) {
            return;
        }
        let originalDuration = stepDataToChange.duration === null ?
            track.trackData.duration : stepDataToChange.duration;
        let durationMultiplier = duration / originalDuration;
        lastDuration.current = duration;
        operationManager.dispatch(
            updateStepParameter(
                track,
                stepDataToChange,
                stepDataToChange.durationParameter,
                duration));
        
        if (resolution.isTriplet) {
            track.getEasyPattern().pattern.syncBuckets(resolution.isTriplet);
        }

        if (state.keyboard.selectedPianoRollSteps !== undefined) {
            // then update each duration by the multiplier
            if (durationMultiplier !== Infinity) {
                let trackDuration = track.trackData.duration;
                state.keyboard.selectedPianoRollSteps
                    .filter(step => step !== stepDataToChange)
                    .forEach(
                        step =>
                            operationManager.dispatch(
                                updateStepParameter(
                                    track,
                                    step,
                                    step.durationParameter,
                                    Math.max(0.1,
                                             step.duration !== null ?
                                             step.duration * durationMultiplier :
                                             trackDuration * durationMultiplier))));
            }
        }

        track.updateSteps();
    }, [track, xScale, resolution, noteIndex]);

    return {onMouseDown, onMouseUp: _onMouseUp, onMouseOver};
};

